import type { SortEntry } from '@/components/data';
import type { PipelineState } from '@/components/ui';
import { isPipelineState } from './api';

/** URL state for the board and list: normalised enums and ids only, never free text. */
export interface LocationSearch {
  /** The board is the default; `list` is the dense table. */
  view?: 'list';
  /** Native execution view is explicit; source-aware sites are the default. */
  workflow?: true;
  /** One stage filter. */
  stage?: PipelineState;
  /** ISO country code. */
  country?: string;
  /** Owner: primary company id. */
  owner?: string;
  /** Show Disqualified, Cancelled and Archived too. */
  closed?: true;
  /** List sort. */
  sort?: string;
  /** Side panel: the site opened over the board (or over a conversation, see SitePanelSearch). */
  site?: string;
  /** A contact or a company: inside the site panel when one is open, else a drawer over the page. */
  contact?: string;
  company?: string;
}

/** Drawer state only, shared by the board/list and the location page. */
export type DrawerSearch = Pick<LocationSearch, 'contact' | 'company'>;

/** The site panel over a page and the person or company opened inside it; conversations share it with the board. */
export type SitePanelSearch = Pick<LocationSearch, 'site' | 'contact' | 'company'>;

export const LIST_SORT_KEYS = ['name', 'last_meaningful_activity_at'] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const id = (v: unknown) => (typeof v === 'string' && UUID.test(v) ? v : undefined);

export function parseSort(raw: string | undefined, allowed: readonly string[]): SortEntry[] {
  if (!raw) return [];
  const out: SortEntry[] = [];
  for (const part of raw.split(',').slice(0, 3)) {
    const desc = part.startsWith('-');
    const key = desc ? part.slice(1) : part;
    if (allowed.includes(key) && !out.some((s) => s.key === key)) out.push({ key, desc });
  }
  return out;
}

export function validateDrawerSearch(raw: Record<string, unknown>): DrawerSearch {
  const out: DrawerSearch = {};
  const contact = id(raw.contact);
  const company = id(raw.company);
  if (contact) out.contact = contact;
  else if (company) out.company = company;
  return out;
}

/** The site panel's state; a person or company only counts inside an open panel. */
export function validateSitePanelSearch(raw: Record<string, unknown>): SitePanelSearch {
  const site = id(raw.site);
  return site ? { site, ...validateDrawerSearch(raw) } : {};
}

export function validateLocationSearch(raw: Record<string, unknown>): LocationSearch {
  // On the board a person or company also opens without a site, as a drawer over the page.
  const out: LocationSearch = { ...validateDrawerSearch(raw), ...validateSitePanelSearch(raw) };
  if (raw.view === 'list') out.view = 'list';
  if (raw.workflow === true || raw.workflow === 'true') out.workflow = true;
  if (isPipelineState(raw.stage)) out.stage = raw.stage;
  if (typeof raw.country === 'string' && /^[A-Za-z]{2}$/.test(raw.country)) out.country = raw.country.toUpperCase();
  const owner = id(raw.owner);
  if (owner) out.owner = owner;
  if (raw.closed === true || raw.closed === 'true' || raw.closed === '1') out.closed = true;
  const sort = parseSort(typeof raw.sort === 'string' ? raw.sort : undefined, LIST_SORT_KEYS);
  if (sort.length) out.sort = sort.map((s) => `${s.desc ? '-' : ''}${s.key}`).join(',');
  return out;
}
